import type { TFunction } from "i18next";
import type { ManualTask, RoleTask } from "@arc/contracts";

/**
 * The legacy hint catalogue (`GetHintPresetByType` → `dashboardHints.*`), one entry per engine kind (ADR-034 D4):
 * `subject` (the manual / role name the row highlights), `short` (the rest of the row sentence), `info`
 * ("Information") and `solve` ("Troubleshooting options"). The legacy's "marked" combinations are a STATE of their
 * own in the rewrite, so the four manual kinds + the two role warnings are the whole catalogue.
 *
 * `info` and `solve` are the **legacy's own strings** (ADR-048 Phase 4 slice 6), not a paraphrase: they are the
 * substance an operator acts on, they already exist in both languages, and they route the nouns through the
 * glossary — so Takeda reads "Die Handlungsanweisung … in dem Bereich …" rather than a re-authored English
 * sentence. Only the compact `short` clause is this rewrite's own, because the legacy's row text carries the
 * subject and its markup inline and does not fit a one-line row.
 */
export interface TaskTexts {
  subject: string;
  short: string;
  info: string;
  solve: string;
}

/**
 * Fill the legacy's POSITIONAL placeholders.
 *
 * The legacy catalogue predates i18next's named interpolation and writes `%1` / `%2` (manual-or-role name, facility
 * name). Those are not interpolation to i18next — it returns them verbatim — so they are substituted here, AFTER
 * `t()` has resolved the string and its `$t(...)` glossary references. Done in this order the tenant's own nouns
 * still apply, which a pre-substitution would break.
 */
function fill(text: string, one: string, two?: string): string {
  return text.replace(/%1/g, one).replace(/%2/g, two ?? "");
}

const MANUAL_HINT = {
  ManualInDraft: { hint: "hintAssignedDraft", short: "tasks.shortInDraft" },
  ManualReadyForReview: { hint: "hintWaitingForReviewLessThanWeek", short: "tasks.shortAwaitingReview" },
  InReviewLongerThanOneWeek: { hint: "hintWaitingOneWeek", short: "tasks.shortWaitingOverWeek" },
  ManualConfirmedAndMarked: { hint: "hintReleasedWithMessage", short: "tasks.shortReleasedWithReport" },
} as const satisfies Record<ManualTask["type"], { hint: string; short: string }>;

const ROLE_HINT = {
  NoHighestFacilityPermissionSet: { hint: "noPermissionAssigned", short: "tasks.shortNoPermissionLevel" },
  NoFacilityPermissionsSet: { hint: "noAreaAssigned", short: "tasks.shortNoFacilities" },
} as const satisfies Record<RoleTask["type"], { hint: string; short: string }>;

export function manualTaskTexts(task: ManualTask, t: TFunction): TaskTexts {
  const { hint, short } = MANUAL_HINT[task.type];
  return {
    subject: task.manualName,
    short: t(short),
    info: fill(t(`dashboard.dashboardHints.${hint}.textTwo` as never), task.manualName, task.facilityName),
    solve: fill(t(`dashboard.dashboardHints.${hint}.textThree` as never), task.manualName, task.facilityName),
  };
}

export function roleTaskTexts(task: RoleTask, t: TFunction): TaskTexts {
  const { hint, short } = ROLE_HINT[task.type];
  return {
    subject: task.roleName,
    short: t(short),
    info: fill(t(`dashboard.dashboardHints.${hint}.textTwo` as never), task.roleName),
    solve: fill(t(`dashboard.dashboardHints.${hint}.textThree` as never), task.roleName),
  };
}
